import React, { useContext, useEffect, useState } from 'react';
import { Box } from 'grommet';

import { DateTime } from 'luxon';

import { MoonLoader } from 'react-spinners';

import { Archive, Trash } from 'grommet-icons';

import { TeamContext } from '@services/Session';

import SHDBackButton from '@ui/BackButton/SHDBackButton';
import SHDButton from '@ui/Button/SHDButton';
import SHDCallout from '@ui/Callout/SHDCallout';
import SHDContainer from '@ui/Container/SHDContainer';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDImage from '@ui/Image/SHDImage';
import SHDText from '@ui/Text/SHDText';

// Types
import type { TeamArchiveOrDeleteProps } from './teamInformation.types';

const TeamArchiveOrDelete: React.FC<TeamArchiveOrDeleteProps> = ({
  canDelete = false,
  isTeamRemoved,
  isTeamRemovalProcessing,
  onCancel,
  onArchive,
  onDelete,
}) => {
  const actionVerb = canDelete
    ? { present: 'Delete', past: 'Deleted' }
    : { present: 'Archive', past: 'Archived' };

  const teamStore = useContext(TeamContext);
  const team = teamStore.fetchTeamInfo;
  const teamLogoUrl = team.imageLogo.small || '/image_default.jpg';
  const teamName = team.nameLong || 'Your Team';
  const teamAgeLevel = team.ageLevel || 'Unknown Age Level';
  const [teamJoinYear, setTeamJoinYear] = useState<string>('2024');

  useEffect(() => {
    if (team && team?.createdTs) {
      setTeamJoinYear(DateTime.fromSeconds(team.createdTs).year.toString());
    }
  }, [team]);

  const actionCopy = canDelete ? (
    <Box gap="small">
      <SHDText style="subhead">
        Deleting your team is permanent and cannot be undone. All players,
        coaches, and managers lose access, and all associated content (clips,
        games, etc.) is permanently removed. The team will no longer be
        accessible or searchable and its URL will be freed for reuse.
      </SHDText>
    </Box>
  ) : (
    <Box gap="small">
      <SHDText style="subhead" color="gray-900">
        By archiving your team, all coaches and team managers (including
        yourself) will lose administrative access to the team.
      </SHDText>
      <SHDText style="subhead" color="gray-900">
        All highlights will be preserved in that they will continue to live on
        player and game pages.
      </SHDText>
    </Box>
  );

  const resultCopy = canDelete ? (
    <SHDText style="subhead" color="gray-900">
      {teamName} has been deleted. You no longer have access to this team.
    </SHDText>
  ) : (
    <SHDText style="subhead" color="gray-900">
      {teamName} has been archived. Highlights are still accessible via game and
      player pages.
    </SHDText>
  );

  return (
    <SHDContainer maxWidth="small">
      {!isTeamRemoved ? (
        <>
          <SHDBackButton onClick={() => onCancel()} />
          <SHDHeading level={2} formatCasing={false} margin={{ top: 'medium' }}>
            {actionVerb.present}&nbsp;
            {teamName}?
          </SHDHeading>
          <Box gap="xlarge">
            {actionCopy}
            <SHDCallout
              background="gray-50"
              direction="row"
              pad="small"
              round="medium"
            >
              <Box>
                <SHDImage
                  alt={`${teamName} logo`}
                  isSquare={true}
                  round="small"
                  src={teamLogoUrl}
                  width={52}
                />
              </Box>
              <Box margin={{ left: 'small' }}>
                <SHDText style="subhead-bold" className="text--center">
                  {teamName}
                </SHDText>
                <Box justify="center">
                  <SHDText
                    style="body-semibold"
                    className="text--center"
                    color="rgba(0,0,0,0.5)"
                  >
                    est. {teamJoinYear} &bull; {teamAgeLevel}
                  </SHDText>
                </Box>
              </Box>
            </SHDCallout>
          </Box>

          <SHDFooter gap="small" width="100%">
            <SHDButton
              primary
              icon={
                isTeamRemovalProcessing ? <MoonLoader size="21px" /> : undefined
              }
              label={`${actionVerb.present} team`}
              disabled={isTeamRemovalProcessing}
              onClick={() => {
                if (canDelete) {
                  onDelete();
                } else {
                  onArchive();
                }
              }}
            />
            <SHDButton
              secondary
              label={`Cancel`}
              disabled={isTeamRemovalProcessing}
              onClick={() => {
                onCancel();
              }}
            />
          </SHDFooter>
        </>
      ) : (
        <Box fill="vertical" flex>
          <Box flex justify="center" align="center" gap="1rem">
            <Box margin={{ bottom: '24px' }}>
              {canDelete ? (
                <Trash color="gray-900" size="90px" />
              ) : (
                <Archive color="gray-900" size="90px" />
              )}
            </Box>
            <SHDHeading level={3}>Team {actionVerb.past}</SHDHeading>
            {resultCopy}
          </Box>
          <SHDFooter>
            <SHDButton
              primary
              label={`Back to home`}
              onClick={() => {
                window.location.href = '/home';
              }}
            />
          </SHDFooter>
        </Box>
      )}
    </SHDContainer>
  );
};

export default TeamArchiveOrDelete;
